import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { UsersSelf } from '@freelancer/datastore/collections';
import { InputType } from '@freelancer/ui/input';
import { Margin } from '@freelancer/ui/margin';
import { SelectItem } from '@freelancer/ui/select';
import { FontType, FontWeight, TextSize } from '@freelancer/ui/text';
import { pattern, required } from '@freelancer/ui/validators';
import { isFormControl } from '@freelancer/utils';
import { DepositMethodApi } from 'api-typings/payments/payments';
import { validateCPF } from './cpf.validator';
import { validateRUT } from './rut.validator';

// FIXME: this is basically a copy of BoletoInfoApi from api-typings
export enum LatinAmericaFormFields {
  NAME = 'name',
  BIRTH_DATE = 'birthdate',
  CPF = 'document',
  RUT = 'chileanDocument',
  EMAIL = 'email',
  PHONE = 'phone',
  ADDRESS = 'address',
  STREET_NUMBER = 'street_number',
  STREET_COMPLEMENT = 'street_complete',
  CITY = 'city',
  STATE = 'state',
  ZIP = 'zip',
  COUNTRY_CODE = 'country_code',
}

enum AdditionalInputTypes {
  SELECT = 'select',
}

interface LatinAmericaInputSetup {
  key: LatinAmericaFormFields;
  type: InputType | AdditionalInputTypes;
  placeholder?: string;
  countries?: readonly string[];
  methods?: readonly string[]; // FIXME: this should contain only DepositMethodApi
  options?: readonly SelectItem[] | readonly string[];
}

interface LatinAmericaFormGroupSetup {
  fieldGroups: readonly (readonly LatinAmericaInputSetup[])[];
}

@Component({
  selector: 'app-latin-america-form',
  template: `
    <ng-container *ngIf="formGroup">
      <ng-container *ngFor="let section of formSetup">
        <fl-grid *ngFor="let group of section.fieldGroups">
          <ng-container *ngFor="let field of group; let last = last">
            <fl-col
              *ngIf="showAdditionalFields(field)"
              [col]="12"
              [colDesktopSmall]="
                group.length === 2 ? 6 : group.length === 3 ? 4 : 12
              "
            >
              <fl-label
                [ngSwitch]="field.key"
                [flMarginBottom]="Margin.XXXSMALL"
                [for]="field.key"
              >
                <ng-container
                  *ngSwitchCase="LatinAmericaFormFields.NAME"
                  i18n="Latin america form label"
                >
                  Full name
                </ng-container>
                <ng-container
                  *ngSwitchCase="LatinAmericaFormFields.BIRTH_DATE"
                  i18n="Latin america form label"
                >
                  Birth date
                </ng-container>
                <ng-container
                  *ngSwitchCase="LatinAmericaFormFields.CPF"
                  i18n="Latin america form label"
                >
                  CPF
                </ng-container>
                <ng-container
                  *ngSwitchCase="LatinAmericaFormFields.RUT"
                  i18n="Latin america form label"
                >
                  RUT
                </ng-container>
                <ng-container
                  *ngSwitchCase="LatinAmericaFormFields.PHONE"
                  i18n="Latin america form label"
                >
                  Phone
                </ng-container>
                <ng-container
                  *ngSwitchCase="LatinAmericaFormFields.EMAIL"
                  i18n="Latin america form label"
                >
                  Email
                </ng-container>
                <ng-container
                  *ngSwitchCase="LatinAmericaFormFields.ADDRESS"
                  i18n="Latin america form label"
                >
                  Address
                </ng-container>
                <ng-container
                  *ngSwitchCase="LatinAmericaFormFields.STREET_NUMBER"
                  i18n="Latin america form label"
                >
                  Street number
                </ng-container>
                <ng-container
                  *ngSwitchCase="LatinAmericaFormFields.STREET_COMPLEMENT"
                  i18n="Latin america form label"
                >
                  Street complement
                </ng-container>
                <ng-container
                  *ngSwitchCase="LatinAmericaFormFields.ZIP"
                  i18n="Latin america form label"
                >
                  ZIP
                  <fl-link
                    flTrackingLabel="latinFormLink: ZIP"
                    [link]="
                      'http://www.buscacep.correios.com.br/servicos/dnec/index.do'
                    "
                  >
                    Don't know your ZIP?
                  </fl-link>
                </ng-container>
                <ng-container
                  *ngSwitchCase="LatinAmericaFormFields.STATE"
                  i18n="Latin america form label"
                >
                  State
                </ng-container>
                <ng-container
                  *ngSwitchCase="LatinAmericaFormFields.CITY"
                  i18n="Latin america form label"
                >
                  City
                </ng-container>
                <ng-container
                  *ngSwitchCase="LatinAmericaFormFields.COUNTRY_CODE"
                  i18n="Latin america form label"
                >
                  Country
                </ng-container>
              </fl-label>
              <ng-container *ngIf="formGroup.controls[field.key] as control">
                <ng-container
                  *ngIf="field.type === AdditionalInputTypes.SELECT"
                >
                  <fl-select
                    *ngIf="isFormControl(control)"
                    flTrackingLabel="{{ 'latinFormDropdown:' + field.key }}"
                    [control]="control"
                    [flMarginBottom]="Margin.XXSMALL"
                    [id]="field.key"
                    [options]="field.options"
                  ></fl-select>
                </ng-container>
                <ng-container
                  *ngIf="field.type !== AdditionalInputTypes.SELECT"
                >
                  <fl-input
                    *ngIf="isFormControl(control)"
                    flTrackingLabel="{{ 'latinFormInput:' + field.key }}"
                    [control]="control"
                    [flMarginBottom]="Margin.XXSMALL"
                    [id]="field.key"
                    [placeholder]="field.placeholder"
                    [type]="field.type"
                  ></fl-input>
                </ng-container>
              </ng-container>
            </fl-col>
          </ng-container>
        </fl-grid>
      </ng-container>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LatinAmericaFormComponent implements OnChanges {
  Margin = Margin;
  AdditionalInputTypes = AdditionalInputTypes;
  LatinAmericaFormFields = LatinAmericaFormFields;
  TextSize = TextSize;
  FontType = FontType;
  FontWeight = FontWeight;
  InputType = InputType;
  isFormControl = isFormControl;

  formGroup: FormGroup;

  @Input() id: string;
  @Input() userInfo: UsersSelf;
  @Input() countriesList: readonly SelectItem[];

  @Output() genericFormGroup = new EventEmitter<FormGroup>();

  formSetup: readonly LatinAmericaFormGroupSetup[];

  statesBrazil = [
    { name: 'Acre', abbrev: 'AC' },
    { name: 'Alagoas', abbrev: 'AL' },
    { name: 'Amapá', abbrev: 'AP' },
    { name: 'Amazonas', abbrev: 'AM' },
    { name: 'Bahia', abbrev: 'BA' },
    { name: 'Ceará', abbrev: 'CE' },
    { name: 'Distrito Federal', abbrev: 'DF' },
    { name: 'Espírito Santo', abbrev: 'ES' },
    { name: 'Goiás', abbrev: 'GO' },
    { name: 'Maranhão', abbrev: 'MA' },
    { name: 'Mato Grosso', abbrev: 'MT' },
    { name: 'Mato Grosso do Sul', abbrev: 'MS' },
    { name: 'Minas Gerais', abbrev: 'MG' },
    { name: 'Pará', abbrev: 'PA' },
    { name: 'Paraíba', abbrev: 'PB' },
    { name: 'Paraná', abbrev: 'PR' },
    { name: 'Pernambuco', abbrev: 'PE' },
    { name: 'Piauí', abbrev: 'PI' },
    { name: 'Rio de Janeiro', abbrev: 'RJ' },
    { name: 'Rio Grande do Norte', abbrev: 'RN' },
    { name: 'Rio Grande do Sul', abbrev: 'RS' },
    { name: 'Rondônia', abbrev: 'RO' },
    { name: 'Roraima', abbrev: 'RR' },
    { name: 'Santa Catarina', abbrev: 'SC' },
    { name: 'São Paulo', abbrev: 'SP' },
    { name: 'Sergipe', abbrev: 'SE' },
    { name: 'Tocantins', abbrev: 'TO' },
  ] as const;

  stateSelectOptionsBrazil: readonly SelectItem[] = this.statesBrazil.map(
    state => ({
      displayText: state.name,
      value: state.abbrev,
    }),
  );

  statesMexico = [
    'Aguascalientes',
    'Baja California',
    'Baja California Sur',
    'Campeche',
    'Chiapas',
    'Chihuahua',
    'Coahuila',
    'Colima',
    'Ciudad de México',
    'Durango',
    'Guanajuato',
    'Guerrero',
    'Hidalgo',
    'Jalisco',
    'México',
    'Michoacán',
    'Morelos',
    'Nayarit',
    'Nuevo León',
    'Oaxaca',
    'Puebla',
    'Querétaro',
    'Quintana Roo',
    'San Luis Potosí',
    'Sinaloa',
    'Sonora',
    'Tabasco',
    'Tamaulipas',
    'Tlaxcala',
    'Veracruz',
    'Yucatán',
    'Zacatecas',
  ] as const;

  statesChile = [
    'Arica and Parinacota',
    'Tarapacá',
    'Atacama',
    'Coquimbo',
    'Valparaíso',
    'Santiago',
    'O’Higgins',
    'Maule',
    'Ñuble',
    'Biobío',
    'Araucania',
    'Los Rios',
    'Los Lagos',
    'Aysén',
    'Magallanes',
  ] as const;

  stateSelectOptionsChile: readonly SelectItem[] = this.statesChile.map(
    state => ({
      displayText: state,
      value: state,
    }),
  );
  stateSelectOptionsMexico: readonly SelectItem[] = this.statesMexico.map(
    state => ({
      displayText: state,
      value: state,
    }),
  );

  constructor(private fb: FormBuilder) {}

  ngOnChanges() {
    // this.countriesList is resolved after ngOnInit() is fired.
    // Moved the setup code here with a condition.
    if (this.countriesList && this.userInfo) {
      const countryName = this.userInfo.address?.country ?? 'Brazil';
      const countryCode = this.countriesList.find(
        c => c.displayText === countryName,
      )?.value as string;
      this.initialiseFormGroup(countryCode);
      this.initiateFormSetup(countryName);
    }
  }

  initialiseFormGroup(countryCode: string) {
    this.formGroup = this.fb.group({
      [LatinAmericaFormFields.ADDRESS]: [
        undefined,
        required($localize`Address is required.`),
      ],
      // Include country code here as backend uses it
      [LatinAmericaFormFields.COUNTRY_CODE]: [countryCode],
      [LatinAmericaFormFields.CITY]: [
        undefined,
        [required($localize`City is required!`)],
      ],
      [LatinAmericaFormFields.BIRTH_DATE]: [
        undefined,
        [
          required($localize`Date of birth is required.`),
          pattern(
            /^\d{2}\/\d{2}\/\d{4}$/,
            $localize`Invalid date type, use DD/MM/YYYY`,
          ),
        ],
      ],
      [LatinAmericaFormFields.EMAIL]: [
        undefined,
        required($localize`Email is a required field.`),
      ],
      [LatinAmericaFormFields.PHONE]: [
        undefined,
        required($localize`Phone is required.`),
      ],
      [LatinAmericaFormFields.STREET_NUMBER]: [
        undefined,
        required($localize`Street number is required.`),
      ],
      [LatinAmericaFormFields.STREET_COMPLEMENT]: undefined,
      [LatinAmericaFormFields.ZIP]: [
        undefined,
        [required($localize`Zip is required!`)],
      ],
    });
    // FIXME: Please, somebody fix this logic :(
    if (this.id === DepositMethodApi.BOLETO) {
      this.formGroup.addControl(
        LatinAmericaFormFields.NAME,
        this.fb.control(
          this.userInfo.firstName && this.userInfo.lastName
            ? `${this.userInfo.firstName} ${this.userInfo.lastName}`
            : '',
          required($localize`Name is required.`),
        ),
      );
    }
    if (this.userInfo.address && this.userInfo.address.country === 'Brazil') {
      this.formGroup.addControl(
        LatinAmericaFormFields.STATE,
        this.fb.control(undefined, required($localize`State is required`)),
      );
      this.formGroup.addControl(
        LatinAmericaFormFields.CPF,
        this.fb.control(undefined, [
          validateCPF(),
          required($localize`A valid CPF is required`),
        ]),
      );
    }
    if (this.userInfo.address && this.userInfo.address.country === 'Mexico') {
      this.formGroup.addControl(
        LatinAmericaFormFields.STATE,
        this.fb.control(undefined, required($localize`State is required`)),
      );
    }
    if (this.userInfo.address && this.userInfo.address.country === 'Chile') {
      this.formGroup.addControl(
        LatinAmericaFormFields.RUT,
        this.fb.control(undefined, [
          validateRUT(),
          required($localize`A valid RUT is required`),
        ]),
      );
      this.formGroup.addControl(
        LatinAmericaFormFields.STATE,
        this.fb.control(undefined, required($localize`State is required`)),
      );
    }
    this.genericFormGroup.emit(this.formGroup);
  }

  showAdditionalFields(field: LatinAmericaInputSetup) {
    const methodAvailable = field.methods
      ? field.methods.includes(this.id)
      : true;
    const country = this.userInfo.address
      ? this.userInfo.address.country || ''
      : '';
    const countryAvailable = field.countries
      ? field.countries.includes(country)
      : true;
    return methodAvailable && countryAvailable;
  }

  initiateFormSetup(userCountry?: string) {
    this.formSetup = [
      {
        fieldGroups: [
          [
            {
              key: LatinAmericaFormFields.NAME,
              type: InputType.TEXT,
              methods: [DepositMethodApi.BOLETO],
            },
          ],
          [
            {
              key: LatinAmericaFormFields.BIRTH_DATE,
              type: InputType.TEXT,
              placeholder: 'DD/MM/YYYY',
            },
            {
              key: LatinAmericaFormFields.CPF,
              type: InputType.NUMBER,
              countries: ['Brazil'],
            },
            {
              key: LatinAmericaFormFields.RUT,
              type: InputType.TEXT,
              countries: ['Chile'],
            },
          ],
        ],
      },
      {
        fieldGroups: [
          [
            {
              key: LatinAmericaFormFields.PHONE,
              type: InputType.NUMBER,
            },
            {
              key: LatinAmericaFormFields.EMAIL,
              type: InputType.EMAIL,
            },
          ],
        ],
      },
    ];

    const groupAddress: LatinAmericaFormGroupSetup = {
      fieldGroups: [
        [
          {
            key: LatinAmericaFormFields.ADDRESS,
            type: InputType.TEXT,
          },
        ],
        [
          {
            key: LatinAmericaFormFields.STREET_NUMBER,
            type: InputType.NUMBER,
          },
          {
            key: LatinAmericaFormFields.STREET_COMPLEMENT,
            type: InputType.TEXT,
          },
        ],
      ],
    };

    if (userCountry === 'Chile') {
      groupAddress.fieldGroups = [
        ...groupAddress.fieldGroups,
        [
          {
            key: LatinAmericaFormFields.CITY,
            type: InputType.TEXT,
          },
          {
            key: LatinAmericaFormFields.STATE,
            type: AdditionalInputTypes.SELECT,
            options: this.stateSelectOptionsChile,
            countries: ['Chile'],
          },
        ],
        [
          {
            key: LatinAmericaFormFields.ZIP,
            type: InputType.TEXT,
          },
          {
            // To prevent the above city field taking the entire width.
            // This field will not be displayed due to dummy country filter.
            key: LatinAmericaFormFields.COUNTRY_CODE,
            type: InputType.TEXT,
            countries: ['dummy'],
          },
        ],
      ];
    } else {
      groupAddress.fieldGroups = [
        ...groupAddress.fieldGroups,
        [
          {
            key: LatinAmericaFormFields.ZIP,
            type: InputType.TEXT,
          },
          {
            key: LatinAmericaFormFields.STATE,
            type: AdditionalInputTypes.SELECT,
            options:
              userCountry === 'Brazil'
                ? this.stateSelectOptionsBrazil
                : this.stateSelectOptionsMexico,
            countries: ['Brazil', 'Mexico'],
          },
        ],
        [
          {
            key: LatinAmericaFormFields.CITY,
            type: InputType.TEXT,
          },
          {
            // To prevent the above city field taking the entire width.
            // This field will not be displayed due to dummy country filter.
            key: LatinAmericaFormFields.COUNTRY_CODE,
            type: InputType.TEXT,
            countries: ['dummy'],
          },
        ],
      ];
    }

    this.formSetup = [...this.formSetup, groupAddress];
  }
}
